import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Share2, Video, Shield, Globe, Monitor } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ClientsCarousel from "@/components/ClientsCarousel";
import ContactForm from "@/components/ContactForm";
import ServiceCard from "@/components/ServiceCard";
import { AnimatedHero } from "@/components/ui/animated-hero";
import WebsiteCreationSection from "@/components/WebsiteCreationSection";
import ResultsSection from "@/components/ResultsSection";
import SEO from "@/components/SEO";
import { InteractiveHoverButton } from "@/components/ui/interactive-hover-button";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useTestimonials } from "@/hooks/useTestimonials";
import bannerPrincipal from "@/assets/banner-principal.png";
import monkTeam from "@/assets/monk-team.jpg";
import mktDigital from "@/assets/mkt-digital.png";
import performanceDigital from "@/assets/performance-digital.png";
import mockupPortfolio from "@/assets/mockup-portfolio.png";
import google5Star from "@/assets/google-5-star.png";

const services = [
{ icon: Target, title: "Gestão de Tráfego Pago", desc: "Criamos campanhas estratégicas no Google, Meta e LinkedIn para atrair clientes e gerar conversões reais." },
{ icon: Share2, title: "Gestão de Redes Sociais", desc: "Planejamento, criação e gerenciamento de conteúdos que engajam e fortalecem sua marca online." },
{ icon: Video, title: "Produção Audiovisual", desc: "Vídeos, fotos e captação com drone para destacar sua empresa e transmitir profissionalismo." },
{ icon: Shield, title: "Registro de Marca", desc: "Registramos sua marca no INPI com segurança e proteção para garantir exclusividade ao seu nome." },
{ icon: Globe, title: "Desenvolvimento de Websites", desc: "Criamos sites modernos, responsivos e otimizados para conversão e experiência do usuário." },
{ icon: Monitor, title: "Sistema para Negócios", desc: "Sistema para organização de empresas, comércios e gestão completa do seu negócio." }];


const Index = () => {
  const { testimonials: dbTestimonials } = useTestimonials();

  const mappedTestimonials = dbTestimonials.map((t, i) => ({
    id: t.id || i + 1,
    name: t.name,
    date: 'Há 2 semanas', // Idealmente viria do banco, mas mantendo conforme imagem
    content: t.content,
    rating: t.rating,
    category: t.service || t.role || t.company || 'Cliente',
    avatar: t.avatar_url || ""
  }));

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Agência de Marketing Digital"
        description="A Monk Studio é uma agência de marketing digital com mais de 13 anos de experiência. Tráfego pago, redes sociais, landing pages, produção audiovisual e muito mais."
        canonical="/" />

      <Navbar />

      {/* Hero with Image Background */}
      <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
        <img
          src={bannerPrincipal}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={1080}
        />
        

        {/* Animated Hero Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-6">
          <AnimatedHero />
        </div>
      </section>

      <WebsiteCreationSection />
      <ResultsSection />

      {/* Services */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="section-divider mb-8" />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center tracking-wide mb-4">
            Inovações para <span style={{ color: '#ffbc00' }}>Seu Negócio</span>
          </h2>
          <div className="text-center mb-16">
            <Link to="/solucoes">
              <InteractiveHoverButton text="CONHEÇA NOSSAS SOLUÇÕES" className="border-[#ffbc00] text-white" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) =>
            <ServiceCard key={s.title} icon={s.icon} title={s.title} desc={s.desc} index={i} />
            )}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Sobre a <span className="text-primary">Monk Studio.</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              A Monk Studio é uma agência especializada em marketing digital, unindo estratégia e criatividade para potencializar negócios. Com mais de 13 anos de experiência, oferecemos soluções completas, incluindo gestão de tráfego pago, redes sociais, captação de imagens e vídeos, identidade visual, criação de marcas e desenvolvimento de sites.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Nosso compromisso é transformar a presença digital de empresas, gerando impacto, autoridade e resultados reais. Seja visto, seja lembrado, seja Monk Studio.
            </p>
            <Link to="/sobre">
              <InteractiveHoverButton text="SAIBA MAIS" className="border-[#ffbc00] text-white" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <img alt="Equipe da agência Monk Studio de marketing digital" className="rounded-lg w-full" loading="lazy" src="/lovable-uploads/bb9e1faa-8caf-459a-976e-ec359aeac56a.png" width={800} height={600} />
          </motion.div>
        </div>
      </section>

      {/* Marketing Digital */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <img src={mktDigital} alt="Estratégias de marketing digital para empresas" className="rounded-lg w-full" loading="lazy" width={800} height={600} />
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Marketing <span className="text-primary">Digital</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              O marketing digital é a chave para o crescimento de qualquer negócio na era online. Com estratégias de tráfego pago, gestão de redes sociais e produção de conteúdo, sua empresa se torna mais visível, conquista clientes e gera resultados reais. Invista no digital e leve sua marca ao próximo nível!
            </p>
            <Link to="/solucoes">
              <InteractiveHoverButton text="CONHEÇA NOSSAS SOLUÇÕES" className="border-[#ffbc00] text-white" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Performance */}
      <section className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Performance <span className="text-primary">Digital</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Transformamos estratégia em resultados. Com tráfego pago, redes sociais e otimização contínua, atraímos, engajamos e convertemos seu público. Nosso foco é maximizar seu investimento e gerar crescimento real para sua empresa no digital.
            </p>
            <Link to="/solucoes">
              <InteractiveHoverButton text="CONHEÇA NOSSAS SOLUÇÕES" className="border-[#ffbc00] text-white" />
            </Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <img alt="Performance digital e otimização de campanhas" className="rounded-lg w-full" loading="lazy" src="/lovable-uploads/cae12f55-2a77-4925-99c3-ba891aa8533c.png" width={800} height={600} />
          </motion.div>
        </div>
      </section>

      {/* Clients */}
      <ClientsCarousel />

      {/* Portfolio */}
      <section className="py-20 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <a href="https://www.behance.net/monkstd/" target="_blank" rel="noopener noreferrer">
              <img src={mockupPortfolio} alt="Portfólio de projetos de design e marketing da Monk Studio" className="rounded-lg w-full hover:scale-[1.02] transition-transform" loading="lazy" width={800} height={600} />
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Nossas <span className="text-primary">criações</span>
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Projetos que conectam marcas ao seu público com estratégia, estética e resultado. Confira nosso portfólio no Behance e veja como transformamos ideias em experiências visuais marcantes.
            </p>
            <Link to="/landingpage#portfolio">
              <InteractiveHoverButton text="ACESSAR PORTFÓLIO" className="border-[#ffbc00] text-white" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection testimonials={mappedTestimonials} />


      {/* Contact */}
      <ContactForm />

      <Footer />
    </div>);

};

export default Index;